class ShippingCaculator extends HTMLElement {
  constructor() {
    super();

    this.needEndLoading = false;
    this.showingErrors = false;
    this.generatedShipping = 0;
    this.isLoading = false;
    this.revision = 0;

    this.button = this.querySelector('button'); // peut être null
    this.addressCountrySelect = this.querySelector('#AddressCountryCalculator');
    this.addressProvinceSelect = this.querySelector('#AddressProvinceCalculator');
    this.addressZipInput = this.querySelector('#AddressZipCalculator');
    this.resultsTag = this.querySelector('.shipping-calculator__results');
    this.resultsList = this.querySelector('.shipping-calculator__results-list');
    this.resultsErrorHeading = this.querySelector('.shipping-calculator__error-heading');
    this.resultsSuccessHeading = this.querySelector('.shipping-calculator__success-heading');

    if (this.button) {
      this.button.addEventListener('click', this.onButtonClick.bind(this));
    }

    this.addEventListener('change', this.hideErrorsTag.bind(this), false);

    this.onCartChangeHandler = this.onCartChange.bind(this);
    document.addEventListener('afterCartChanged', this.onCartChangeHandler);
    document.addEventListener('afterUpdateQuantity', this.onCartChangeHandler);
  }

  connectedCallback() {
    document.addEventListener('afterCartChanged', this.onCartChangeHandler);
    document.addEventListener('afterUpdateQuantity', this.onCartChangeHandler);
    this.setupCountries();
    this.setupAutoCalculation();
    if (this.closest('smart-form') || this.addressZipInput?.value.trim()) this.scheduleCalculation();
  }

  disconnectedCallback() {
    clearTimeout(this.calculationTimer);
    this.controller?.abort();
    this.revision += 1;
    (this.addressFields || []).forEach(field => {
      field.removeEventListener('input', this.addressChangeHandler);
      field.removeEventListener('change', this.addressChangeHandler);
    });
    document.removeEventListener('afterCartChanged', this.onCartChangeHandler);
    document.removeEventListener('afterUpdateQuantity', this.onCartChangeHandler);
  }

  setupCountries() {
    if (Shopify && Shopify.CountryProvinceSelector && this.addressCountrySelect && this.addressProvinceSelect) {
      // eslint-disable-next-line no-new
      new Shopify.CountryProvinceSelector(this.addressCountrySelect.id, this.addressProvinceSelect.id, {
        hideElement: 'AddressProvinceContainerCalculator'
      });
    }
  }

  /**
   * Calcul automatique dès que pays + code postal sont remplis
   */
  setupAutoCalculation() {
    this.addressFields = [this.addressCountrySelect, this.addressProvinceSelect, this.addressZipInput].filter(Boolean);
    this.addressChangeHandler = () => this.scheduleCalculation();
    this.addressFields.forEach(field => {
      field.addEventListener('input', this.addressChangeHandler);
      field.addEventListener('change', this.addressChangeHandler);
    });
  }

  addressKey() {
    return JSON.stringify([this.addressCountrySelect, this.addressProvinceSelect, this.addressZipInput]
      .map(field => field ? field.value.trim() : ''));
  }

  cartSignature(cart) {
    return JSON.stringify([cart.currency, cart.total_price, cart.requires_shipping,
      (cart.items || []).map(item => [item.key, item.variant_id, item.quantity, item.final_line_price, item.requires_shipping]),
      cart.cart_level_discount_applications || []]);
  }

  emit(name, detail = {}) {
    this.dispatchEvent(new CustomEvent('smart-form:shipping-' + name, { bubbles: true, detail }));
  }

  scheduleCalculation() {
    clearTimeout(this.calculationTimer);
    this.controller?.abort();
    this.revision += 1;
    this.emit('invalidated');
    this.calculationTimer = setTimeout(() => this.calculateShippingRates(), 400);
  }

  onCartChange() {
    if (this.resultsTag) {
      this.resultsTag.classList.add('hidden');
    }
    this.scheduleCalculation();
  }

  serialize(obj, prefix) {
    var str = [], p;
    for (p in obj) {
      if (obj.hasOwnProperty(p)) {
        var k = prefix ? prefix + '[' + p + ']' : p;
        var v = obj[p];
        str.push((v !== null && typeof v === 'object')
          ? this.serialize(v, k)
          : encodeURIComponent(k) + '=' + encodeURIComponent(v));
      }
    }
    return str.join('&');
  }

  startLoading() {
    this.needEndLoading = false;
    this.isLoading = true;

    this.emit('start');
    if (this.button) {
      this.button.setAttribute('aria-disabled', true);
      this.button.classList.add('loading');
    }
    const spinner = this.querySelector('.loading-overlay__spinner');
    if (spinner) {
      spinner.classList.remove('hidden');
    }
  }

  endLoading() {
    if (!this.needEndLoading) return;

    this.needEndLoading = false;
    this.isLoading = false;

    if (this.button) {
      this.button.setAttribute('aria-disabled', false);
      this.button.classList.remove('loading');
    }
    const spinner = this.querySelector('.loading-overlay__spinner');
    if (spinner) {
      spinner.classList.add('hidden');
    }
  }

  dispatchRatesForSmartForm(list, context) {
    if (!list || !Array.isArray(list.shipping_rates)) return;

    const rates = list.shipping_rates.map((item, index) => ({
      id: item.handle || item.service_code || String(index),
      name: item.presentment_name || item.name || '',
      price: item.price,
      delivery_days: item.delivery_days || []
    }));

    this.emit('rates', { rates, ...context });
  }

  appendResultNode(text) {
    if (!this.resultsList) return;
    const li = document.createElement('li');
    const node = document.createTextNode(text);
    li.appendChild(node);
    this.resultsList.appendChild(li);
  }

  handleResultsList(list) {
    if (!this.resultsList || !this.resultsTag) return;

    this.resultsList.innerHTML = '';
    if (!list.shipping_rates || list.shipping_rates.length === 0) {
      if (this.resultsSuccessHeading) {
        this.resultsSuccessHeading.classList.add('hidden');
      }
      this.appendResultNode(this.dataset.doNotShipMessage || '');
      this.resultsTag.classList.remove('hidden');
      return;
    }

    for (var key in list) {
      if (Array.isArray(list[key])) {
        list[key].forEach((item) => {
          if (typeof item === 'object') {
            const li = document.createElement('li');
            const spanPrice = document.createElement('span');
            spanPrice.classList.add('price');
            const span = document.createElement('span');
            span.classList.add('cart-item__price', 'heading-font');
            const text = (item.presentment_name || '') + ': ';
            span.innerHTML = Shopify.showPrice(item.price * 100);
            spanPrice.append(span);
            const node = document.createTextNode(text);
            li.appendChild(node);
            li.appendChild(spanPrice);
            if (item.delivery_days && item.delivery_days.length > 0) {
              const spanDate = document.createElement('span');
              spanDate.classList.add('caption', 'shipping-calculator__date');
              var spanDateText = '( ';
              if (item.delivery_days[0] == item.delivery_days[1]) {
                if (parseInt(item.delivery_days[0]) <= 1) {
                  spanDateText += this.dataset.deliveryDateOne.replace('[count]', item.delivery_days[0]);
                } else {
                  spanDateText += this.dataset.deliveryDateMany.replace('[count]', item.delivery_days[0]);
                }
              } else {
                spanDateText += this.dataset.deliveryDateRange
                  .replace('[minimum]', item.delivery_days[0])
                  .replace('[maximum]', item.delivery_days[1]);
              }
              spanDateText += ' )';
              const nodeSpanDateText = document.createTextNode(spanDateText);
              spanDate.appendChild(nodeSpanDateText);
              li.appendChild(spanDate);
            }
            this.resultsList.appendChild(li);
          } else {
            this.appendResultNode(item);
          }
        });
      } else {
        this.appendResultNode(list[key]);
      }
    }
    this.resultsTag.classList.remove('hidden');
  }

  handleErrorResponse(errors) {
    this.generatedShipping = 0;
    this.showingErrors = true;
    if (this.resultsErrorHeading) {
      this.resultsErrorHeading.classList.remove('hidden');
    }
    if (this.resultsSuccessHeading) {
      this.resultsSuccessHeading.classList.add('hidden');
    }
    this.handleResultsList(errors);
  }

  handleSuccessResponse(list) {
    this.generatedShipping = 0;
    this.showingErrors = false;
    if (this.resultsSuccessHeading) {
      this.resultsSuccessHeading.classList.remove('hidden');
    }
    if (this.resultsErrorHeading) {
      this.resultsErrorHeading.classList.add('hidden');
    }
    this.handleResultsList(list);
  }

  hideErrorsTag() {
    if (this.showingErrors && this.resultsTag) {
      this.resultsTag.classList.add('hidden');
      this.showingErrors = false;
    }
  }

  /**
   * API interne : lance la requête de calcul des tarifs
   * (utilisée par le bouton si présent, et par le calcul auto)
   */

  async requestJson(url, options, signal) {
    const controller = new AbortController();
    const abort = () => controller.abort();
    signal.addEventListener('abort', abort, { once: true });
    if (signal.aborted) controller.abort();
    const timer = setTimeout(abort, 12000);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal, cache: 'no-store' });
      if (!response.ok) throw new Error('Le calcul de livraison est indisponible. Veuillez réessayer.');
      const text = await response.text();
      return text.trim() ? JSON.parse(text) : null;
    } finally {
      clearTimeout(timer);
      signal.removeEventListener('abort', abort);
    }
  }

  async pollAsyncShippingRates(params, signal) {
    for (let i = 0; i < 15; i++) {
      if (signal.aborted) throw new Error('Calcul annulé');
      const data = await this.requestJson(
        `${routes.cart_url}/async_shipping_rates.json?` + this.serialize(params),
        fetchConfig('json', 'GET'), signal
      );
      if (data && Array.isArray(data.shipping_rates)) return data;
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    throw new Error('Le calcul des frais de port prend trop de temps. Veuillez réessayer.');
  }

  async calculateShippingRates() {
    clearTimeout(this.calculationTimer);
    this.controller?.abort();
    const revision = ++this.revision;
    this.controller = new AbortController();
    const signal = this.controller.signal;
    const current = () => revision === this.revision && !signal.aborted;
    const addressKey = this.addressKey();
    const controller = this.controller;
    const overallTimer = setTimeout(() => controller.abort(), 35000);
    this.startLoading();
    try {
      const cart = await this.requestJson(`${routes.cart_url}.js`, { credentials: 'same-origin' }, signal);
      if (!current()) return;
      if (!cart || !Array.isArray(cart.items) || !cart.items.length) {
        throw new Error('Votre panier est vide. Ajoutez un article avant de demander un devis.');
      }
      const context = { addressKey, cartSignature: this.cartSignature(cart), requiresShipping: cart.items.some(item => item.requires_shipping !== false) };
      if (!context.requiresShipping) {
        this.dispatchRatesForSmartForm({ shipping_rates: [] }, context);
        return;
      }
      if (!this.addressCountrySelect?.value.trim() || !this.addressZipInput?.value.trim()) {
        throw new Error('Renseignez le pays et le code postal pour calculer la livraison.');
      }
      const params = { shipping_address: {
        country: this.addressCountrySelect.value,
        province: this.addressProvinceSelect ? this.addressProvinceSelect.value : '',
        zip: this.addressZipInput.value.trim()
      } };
      await this.requestJson(`${routes.cart_url}/prepare_shipping_rates.json`, {
        ...fetchConfig('json', 'POST'), body: JSON.stringify(params)
      }, signal);
      if (!current()) return;
      const data = await this.pollAsyncShippingRates(params, signal);
      if (!current() || addressKey !== this.addressKey()) return;
      this.dispatchRatesForSmartForm(data, context);
      this.handleSuccessResponse(data);
    } catch (error) {
      if (revision !== this.revision) return;
      const message = signal.aborted
        ? 'Le calcul des frais de port a expiré. Veuillez réessayer.'
        : (error.message || 'Impossible de calculer la livraison.');
      this.emit('errors', { errors: { error: message } });
      if (this.resultsList && this.resultsTag) {
        this.resultsList.replaceChildren();
        this.appendResultNode(message);
        this.resultsTag.classList.remove('hidden');
      }
    } finally {
      clearTimeout(overallTimer);
      if (revision === this.revision) {
        this.needEndLoading = true;
        this.endLoading();
      }
    }
  }

  /**
   * Compat : si un bouton existe encore et est cliqué, on réutilise la même logique.
   */
  onButtonClick() {
    this.calculateShippingRates();
  }
}

if (!customElements.get('shipping-calculator')) {
  customElements.define('shipping-calculator', ShippingCaculator);
}
